<template>
  <div class="scale">
    <div class="scaleValues">
      <div
        v-for="(segment, index) in normalizedSegments"
        :key="segment.label + index"
        class="scaleSegmentLabel"
        :style="segmentStyle(segment)"
      >
        <div class="scaleElementItem">{{ segment.label }}</div>
      </div>
    </div>
    <div class="scaleMark">
      <div
        v-for="(segment, segmentIndex) in normalizedSegments"
        :key="'scale-segment-' + segmentIndex"
        class="scaleSegmentMarks"
        :style="segmentStyle(segment)"
      >
        <div
          v-for="tickIndex in ticksPerSegment(segment)"
          :key="'scale-tick-' + segmentIndex + '-' + tickIndex"
          class="scaleElement"
          :class="{ 'scaleElement--hidden': shouldHideTick(segmentIndex, tickIndex - 1) }"
        ></div>
      </div>
    </div>
  </div>
</template>

<script>
// @group Timeline
// Editor time scale
export default {
  name: "ScaleLabels",
  props: {
    // Time segments with label & duration
    segments: {
      type: Array,
      default: () => []
    },
    // Count of scale marks between two labels
    resolution: {
      type: Number,
      default: 10
    }
  },
  computed: {
    normalizedSegments() {
      return this.segments.map(segment => {
        if (segment && typeof segment === "object" && !Array.isArray(segment)) {
          const size = Number(segment.size);
          const ticks = Number(segment.ticks);
          const hasCustomTicks = Object.prototype.hasOwnProperty.call(
            segment,
            "ticks"
          );

          return {
            label: segment.label ?? "",
            size: Number.isFinite(size) && size > 0 ? size : 0,
            ticks: Number.isFinite(ticks) && ticks >= 0 ? ticks : this.resolution,
            hasCustomTicks
          };
        }

        return {
          label: typeof segment === "string" ? segment : "",
          size: 1,
          ticks: this.resolution,
          hasCustomTicks: false
        };
      });
    },
    totalSize() {
      return this.normalizedSegments.reduce(
        (total, segment) => total + segment.size,
        0
      );
    },
    baseSegmentSize() {
      const segment = this.normalizedSegments.find(item => item.size > 0);
      return segment ? segment.size : 0;
    }
  },
  methods: {
    segmentStyle(segment) {
      const width = this.segmentWidth(segment.size);
      return {
        flexBasis: width,
        maxWidth: width,
        width
      };
    },
    segmentWidth(size) {
      const segmentsCount = this.normalizedSegments.length || 1;

      if (this.totalSize <= 0) {
        return `${100 / segmentsCount}%`;
      }

      if (!size) {
        return "0%";
      }

      return `${(size / this.totalSize) * 100}%`;
    },
    ticksPerSegment(segment) {
      let ticks = Math.max(segment.ticks ?? this.resolution, 1);

      if (!segment.hasCustomTicks) {
        const baseSize = this.baseSegmentSize;

        if (baseSize > 0 && segment.size >= 0) {
          const ratio = segment.size / baseSize;
          const scaledTicks = Math.round(this.resolution * ratio);
          ticks = Math.max(scaledTicks, 1);
        }
      } else {
        ticks = Math.max(Math.round(ticks), 1);
      }

      return ticks + 1;
    },
    shouldHideTick(segmentIndex, tickIndex) {
      return segmentIndex > 0 && tickIndex === 0;
    }
  }
};
</script>

<style scoped lang="scss">
.scale {
  margin: 2px 0 0;
}

.scaleValues {
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  font-weight: lighter;
}

.scaleMark {
  width: 100%;
  height: 10px;
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

.scaleSegmentLabel,
.scaleSegmentMarks {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  flex: 0 0 auto;
}

.scaleSegmentMarks {
  flex-direction: row;
  align-items: flex-end;
  justify-content: space-between;
}

.scaleElement {
  width: 2px;
  height: 5px;
  margin-right: -1px;
  background-color: rgba(255, 255, 255, 0.2);
}

.scaleElement:last-child {
  height: 10px;
}

.scaleElementUnit {
  height: 10px;
}

.scaleElementItem {
  text-align: start;
  font-size: 10pt;
}

.scaleElement--hidden {
  visibility: hidden;
}
</style>
